import { useCommitmentSigning } from '@/composables/useCommitmentSigning'
import { PlatformType } from '@/enums/business'
import { LOGIN_TOKEN } from '@/global/constants'
import router from '@/router'
import {
  accountLoginOutRequest,
  accountLoginRequest,
  getCaptcha,
  getCurrentOrgBrand,
  getPlatformInfo,
  getUserInfoById,
  mockLoginTicketRequest,
  putPlatformSelection,
  smsLoginRequest
} from '@/service/login/login'
import { useFileStore } from '@/store/file/file'
import type { IAccount } from '@/types'
import { localCache } from '@/utils/cache'
import { dialog, message } from '@/utils/naive-instance'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import { createTicketSingleFlight } from './mock-login-flight'
import {
  activateMockLoginSession,
  clearMockLoginSession,
  getMockLoginSession,
  restoreOriginalLoginToken,
  type MockLoginSession
} from './mock-login-session'

type PostLoginReminderType = 'changePassword' | 'bindMobile' | 'realName'

interface CurrentOrgBrand {
  logo: string
  name: string
}

interface MockLoginActionResult {
  success: boolean
  message?: string
  redirectPath?: '/home' | '/login'
}

interface RestoreOriginalSessionResult {
  restored: boolean
  redirectPath: '/home' | '/login'
}

interface ILoginState {
  token: string | null
  userInfo: any
  platFormInfo: any
  redirectPath: string
  loginModalVisible: boolean
  changePasswordModalVisible: boolean
  bindMobileModalVisible: boolean
  realNameReminderVisible: boolean
  isLoggingOut: boolean
  skipNextLoginAutoCaptcha: boolean
  skipChangePwd: boolean
  postLoginReminderQueue: PostLoginReminderType[]
  activePostLoginReminder: PostLoginReminderType | null
  currentOrgBrand: CurrentOrgBrand | null
  platformSwitchKey: number
  mockLoginSession: MockLoginSession | null
  isRestoringMockLogin: boolean
}

const runMockLoginTicketOnce = createTicketSingleFlight<MockLoginActionResult>()
let restoreOriginalSessionFlight: Promise<RestoreOriginalSessionResult> | null =
  null

const useLoginStore = defineStore('login', {
  state: (): ILoginState => ({
    token: localCache.getCache(LOGIN_TOKEN),
    userInfo: localCache.getCache('userInfo'),
    platFormInfo: localCache.getCache('platFormInfo'),
    loginModalVisible: false,
    changePasswordModalVisible: false,
    bindMobileModalVisible: false,
    realNameReminderVisible: false,
    isLoggingOut: false,
    skipNextLoginAutoCaptcha: false,
    skipChangePwd: false,
    postLoginReminderQueue: [],
    activePostLoginReminder: null,
    redirectPath: '',
    currentOrgBrand:
      localCache.getCacheWithType<CurrentOrgBrand>('currentOrgBrand') || null,
    platformSwitchKey: 0,
    mockLoginSession: getMockLoginSession(),
    isRestoringMockLogin: false
  }),
  getters: {
    isMockLogin: (state) => Boolean(state.mockLoginSession),
    platformSelection: (state) => state.platFormInfo?.selection,
    platformType(state) {
      const raw = state.platFormInfo?.selection?.platformType
      const num = Number(raw ?? 0)
      return num as PlatformType | 0
    },
    isSelfStudy(): boolean {
      return this.platformType === PlatformType.SELF_STUDY
    },
    isAdultEducation(): boolean {
      return this.platformType === PlatformType.ADULT_EDUCATION
    },
    isComprehensiveEvaluation(): boolean {
      return this.platformType === PlatformType.COMPREHENSIVE_EVALUATION
    },
    isStudyPlatform(): boolean {
      return [PlatformType.SELF_STUDY, PlatformType.ADULT_EDUCATION].includes(
        this.platformType as PlatformType
      )
    }
  },
  actions: {
    setUserInfo(token: string, userInfo: any, platFormInfo: any) {
      this.token = token
      this.userInfo = userInfo
      this.platFormInfo = platFormInfo
      localCache.setCache(LOGIN_TOKEN, token)
      localCache.setCache('userInfo', userInfo)
      localCache.setCache('platFormInfo', platFormInfo)
    },
    clearUserData() {
      this.userInfo = null
      this.platFormInfo = null
      this.currentOrgBrand = null
      this.platformSwitchKey = 0
      useFileStore().resetFileState()
      this.clearPostLoginReminders()
      localCache.removeCache('userInfo')
      localCache.removeCache('platFormInfo')
      localCache.removeCache('currentOrgBrand')
    },
    clearUserInfo() {
      this.token = null
      localCache.removeCache(LOGIN_TOKEN)
      this.clearUserData()
    },
    setRedirectPath(path: string): void {
      this.redirectPath = path
    },
    async loginAccountAction(account: IAccount) {
      try {
        let loginResult
        if (account?.smsCode) {
          // 短信验证码
          loginResult = await smsLoginRequest(account)
        } else {
          loginResult = await accountLoginRequest(account)
        }
        clearMockLoginSession()
        this.mockLoginSession = null
        this.token = loginResult.data.accessToken
        localCache.setCache(LOGIN_TOKEN, this.token)
        await this.fetchUserInfo(loginResult.data.accessToken)
        await this.fetchOrgBrandInfo()
        await this.refreshExamAppliedStatus()
        this.preparePostLoginReminders(this.userInfo)
        ElMessage.success(loginResult.msg || '登录成功')
        this.closeLoginModal()
        return { success: true }
      } catch (error: any) {
        console.error('登录过程出错:', error)
        return { success: false, message: error.message || '登录失败，请重试' }
      }
    },
    async mockLoginTokenAction(
      mockToken: string
    ): Promise<MockLoginActionResult> {
      const normalizedToken = mockToken.trim()
      const activeSession = this.mockLoginSession ?? getMockLoginSession()

      if (
        activeSession?.mockToken &&
        activeSession.mockToken !== normalizedToken
      ) {
        try {
          await accountLoginOutRequest(activeSession.mockToken)
        } catch (error) {
          console.error('切换拟登录账号时注销旧会话失败:', error)
        }
      }

      try {
        const session = activateMockLoginSession(normalizedToken)
        this.mockLoginSession = session
        this.clearUserData()
        this.token = session.mockToken
        localCache.setCache(LOGIN_TOKEN, session.mockToken)

        await this.fetchUserInfo(session.mockToken)
        await this.fetchOrgBrandInfo()
        await this.refreshExamAppliedStatus()
        this.preparePostLoginReminders(this.userInfo)
        this.closeLoginModal()
        return { success: true }
      } catch (error: any) {
        console.error('拟登录过程出错:', error)
        const restoreResult = await this.restoreOriginalSessionAction()
        return {
          success: false,
          message: error.message || '拟登录失败，请重试',
          redirectPath: restoreResult.redirectPath
        }
      }
    },
    async mockLoginTicketAction(
      ticket: string
    ): Promise<MockLoginActionResult> {
      return runMockLoginTicketOnce(ticket, async () => {
        try {
          const loginResult = await mockLoginTicketRequest({ ticket })
          const accessToken = loginResult?.data?.accessToken
          if (!accessToken) throw new Error('拟登录失败，未获取到登录凭证')
          return await this.mockLoginTokenAction(accessToken)
        } catch (error: any) {
          console.error('拟登录凭证兑换失败:', error)
          return {
            success: false,
            message: error.message || '拟登录失败，请重试',
            redirectPath: this.token ? '/home' : '/login'
          }
        }
      })
    },
    async restoreOriginalSessionAction(): Promise<RestoreOriginalSessionResult> {
      if (restoreOriginalSessionFlight) return restoreOriginalSessionFlight

      this.isRestoringMockLogin = true
      restoreOriginalSessionFlight = (async () => {
        const activeSession = this.mockLoginSession ?? getMockLoginSession()
        if (!activeSession) {
          return {
            restored: Boolean(this.token),
            redirectPath: this.token ? '/home' : '/login'
          }
        }

        this.clearUserData()
        const originalToken = restoreOriginalLoginToken()
        this.mockLoginSession = null
        this.token = originalToken

        if (!originalToken) {
          localCache.removeCache(LOGIN_TOKEN)
          return { restored: false, redirectPath: '/login' }
        }

        try {
          localCache.setCache(LOGIN_TOKEN, originalToken)
          await this.fetchUserInfo(originalToken)
          await this.fetchOrgBrandInfo()
          await this.refreshExamAppliedStatus()
          this.preparePostLoginReminders(this.userInfo)
          this.closeLoginModal()
          return { restored: true, redirectPath: '/home' }
        } catch (error) {
          console.error('恢复原登录账号失败:', error)
          this.clearUserInfo()
          return { restored: false, redirectPath: '/login' }
        }
      })()

      try {
        return await restoreOriginalSessionFlight
      } finally {
        restoreOriginalSessionFlight = null
        this.isRestoringMockLogin = false
      }
    },
    async exitMockLoginAction() {
      const activeSession = this.mockLoginSession ?? getMockLoginSession()
      if (!activeSession) {
        const redirectPath = this.token ? '/home' : '/login'
        await router.replace(redirectPath)
        return {
          logoutFailed: false,
          restored: Boolean(this.token),
          redirectPath
        }
      }

      let logoutFailed = false
      try {
        const result = await accountLoginOutRequest(activeSession.mockToken)
        if (result?.code !== 0) throw new Error(result?.msg || '退出失败')
      } catch (error) {
        logoutFailed = true
        console.error('拟登录服务端会话注销失败:', error)
      }

      const restoreResult = await this.restoreOriginalSessionAction()
      if (logoutFailed) {
        message.warning('已恢复原账号，但拟登录服务端会话注销失败')
      } else if (restoreResult.restored) {
        message.success('已退出拟登录并恢复原账号')
      } else {
        message.success('已退出拟登录')
      }
      await router.replace(restoreResult.redirectPath)
      return {
        logoutFailed,
        restored: restoreResult.restored,
        redirectPath: restoreResult.redirectPath
      }
    },
    async handleMockLoginExpiredAction() {
      const restoreResult = await this.restoreOriginalSessionAction()
      if (restoreResult.restored) {
        message.warning('拟登录已失效，已恢复原账号')
      } else {
        message.error('拟登录已失效，请重新登录')
      }
      await router.replace(restoreResult.redirectPath)
      return restoreResult
    },
    async refreshUserInfoAction(token?: string | null) {
      const res = await getUserInfoById(token ?? this.token)
      this.userInfo = res.data
      localCache.setCache('userInfo', this.userInfo)
      return this.userInfo
    },
    async runWithUserInfoRefresh<T>(action: () => Promise<T>) {
      const res: any = await action()
      if (res?.code === 0) {
        await this.refreshUserInfoAction()
      }
      return res as T
    },
    async fetchUserInfo(token: string | null) {
      await this.refreshUserInfoAction(token)
      await this.fetchPlatformInfo(token)
    },
    async fetchPlatformInfo(token: string | null) {
      const res = await getPlatformInfo(token)
      this.platFormInfo = res.data
      localCache.setCache('platFormInfo', this.platFormInfo)
    },
    async refreshExamAppliedStatus() {
      try {
        await useCommitmentSigning().checkExamApplied()
      } catch (error) {
        console.error('获取考试报名状态失败:', error)
      }
    },
    async switchPlatformAction(data: {
      tenantId: string | number
      platformId: string | number
      studentEnrollmentId: string | number
    }) {
      try {
        const res = await putPlatformSelection(data)
        if (res.code !== 0) throw new Error(res.msg)
        await this.fetchUserInfo(this.token)
        await this.fetchOrgBrandInfo()
        await this.refreshExamAppliedStatus()
        this.platformSwitchKey += 1
        this.preparePostLoginReminders(this.userInfo)
        message.success('平台切换成功')
        return true
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async logoutAccountAction() {
      try {
        if (this.isLoggingOut) {
          return { success: false, message: '正在退出登录，请勿重复操作' }
        }

        const isMockLogin = this.isMockLogin

        dialog.create({
          title: isMockLogin ? '退出拟登录' : '退出登录',
          content: isMockLogin
            ? '确定要退出当前学生并恢复原登录账号吗？'
            : '确定要退出登录吗？',
          positiveText: '确定',
          negativeText: '取消',
          draggable: true,
          closeOnEsc: false,
          showIcon: false,
          class: 'custom_dialog',
          contentClass: 'custom_dialog--content',
          titleClass: 'custom_dialog--title',
          actionClass: 'custom_dialog--action',
          onPositiveClick: async () => {
            const loadingMessage = message.loading(
              isMockLogin ? '正在退出拟登录...' : '正在退出登录...',
              {
                duration: 0
              }
            )
            try {
              this.isLoggingOut = true
              if (isMockLogin) {
                await this.exitMockLoginAction()
                return
              }

              await accountLoginOutRequest()
              message.success('登出成功')
            } catch (error) {
              console.error('登录退出过程出错:', error)
              message.warning('退出登录接口异常，已清除本地登录状态')
            } finally {
              loadingMessage.destroy()
              try {
                if (!isMockLogin) {
                  await this.logoutAction({ skipLoginAutoCaptcha: true })
                }
              } finally {
                this.isLoggingOut = false
              }
            }
          },
          onNegativeClick: () => {
            message.info('已取消退出')
          }
        })

        return { success: true }
      } catch (error: any) {
        console.error('登录退出过程出错:', error)
        return { success: false, message: error.message || '退出失败，请重试' }
      }
    },
    loadLocalCacheAction() {
      const token = localCache.getCache(LOGIN_TOKEN)
      const userInfo = localCache.getCache('userInfo')
      const platFormInfo = localCache.getCache('platFormInfo')
      const currentOrgBrand =
        localCache.getCacheWithType<CurrentOrgBrand>('currentOrgBrand')

      this.token = token
      this.userInfo = userInfo
      this.platFormInfo = platFormInfo
      this.currentOrgBrand = currentOrgBrand || null
      this.mockLoginSession = getMockLoginSession()
    },

    async logoutAction(options: { skipLoginAutoCaptcha?: boolean } = {}) {
      this.clearUserInfo()
      this.closeLoginModal()
      this.redirectPath = ''
      this.skipNextLoginAutoCaptcha = options.skipLoginAutoCaptcha === true
      await router.replace('/login')
    },
    consumeSkipLoginAutoCaptcha() {
      const shouldSkip = this.skipNextLoginAutoCaptcha
      this.skipNextLoginAutoCaptcha = false
      return shouldSkip
    },
    async getCaptcha() {
      try {
        const result = await getCaptcha()
        return result
      } catch (error) {
        console.error('获取验证码失败:', error)
        ElMessage.error('获取验证码失败，请重试')
        return null
      }
    },
    openLoginModal() {
      this.loginModalVisible = true
    },
    closeLoginModal() {
      this.loginModalVisible = false
    },
    preparePostLoginReminders(userInfo: any) {
      const reminderQueue: PostLoginReminderType[] = []

      if (userInfo?.needChangePassword) {
        reminderQueue.push('changePassword')
      }

      if (userInfo?.hasMobileBound === false) {
        reminderQueue.push('bindMobile')
      }

      if (userInfo?.isRealNameVerified === false) {
        reminderQueue.push('realName')
      }

      this.postLoginReminderQueue = reminderQueue
      this.activePostLoginReminder = null
      this.showCurrentPostLoginReminder()
    },
    startPostLoginReminders() {
      if (this.activePostLoginReminder || !this.postLoginReminderQueue.length) {
        return
      }

      this.activePostLoginReminder = this.postLoginReminderQueue[0]
      this.showCurrentPostLoginReminder()
    },
    showCurrentPostLoginReminder() {
      this.changePasswordModalVisible =
        this.activePostLoginReminder === 'changePassword'
      this.bindMobileModalVisible =
        this.activePostLoginReminder === 'bindMobile'
      this.realNameReminderVisible = this.activePostLoginReminder === 'realName'
    },
    advancePostLoginReminder() {
      if (!this.activePostLoginReminder) {
        return
      }

      this.postLoginReminderQueue = this.postLoginReminderQueue.slice(1)
      this.activePostLoginReminder = this.postLoginReminderQueue[0] ?? null
      this.showCurrentPostLoginReminder()
    },
    completeCurrentPostLoginReminder() {
      if (this.activePostLoginReminder === 'changePassword' && this.userInfo) {
        this.userInfo.needChangePassword = false
        localCache.setCache('userInfo', this.userInfo)
      }

      if (this.activePostLoginReminder === 'bindMobile' && this.userInfo) {
        this.userInfo.hasMobileBound = true
        localCache.setCache('userInfo', this.userInfo)
      }

      this.advancePostLoginReminder()
    },
    // 获取机构logo name
    async fetchOrgBrandInfo() {
      const res = await getCurrentOrgBrand()
      this.currentOrgBrand = res?.data
      localCache.setCache('currentOrgBrand', this.currentOrgBrand)
    },
    skipCurrentPostLoginReminder() {
      this.advancePostLoginReminder()
    },
    clearPostLoginReminders() {
      this.postLoginReminderQueue = []
      this.activePostLoginReminder = null
      this.showCurrentPostLoginReminder()
    },
    openChangePasswordModal() {
      this.changePasswordModalVisible = true
    },
    loseChangePasswordModal() {
      this.changePasswordModalVisible = false
      if (this.userInfo) this.userInfo.needChangePassword = false
    },
    skipChangePassword() {
      this.skipChangePwd = true
      this.skipCurrentPostLoginReminder()
    },
    resetSkipStatus() {
      this.skipChangePwd = false
    }
  }
})

export default useLoginStore
